import React, { useState, useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import io from 'socket.io-client';
import { API_URL, apiGet, apiPatch, apiPost } from '../api';
import { formatNumberPrecise } from '../Utils';
import { WHEEL_SECTIONS_CONFIG } from './constants';
import DailyHistoryViewer from './TeenPattiame/DailyHistoryViewer';
import './Admin.css';

// Game runs in zoneserver (/greedyItems namespace); settings/stats via /api/games.
const GAME_KEY = 'greedyItems';
const SOCKET_URL = `${API_URL}/${GAME_KEY}`;
const BIAS_OPTIONS = [2, 3, 4, 5];

const Greedy = () => {
    const socketRef = useRef(null);
    const [isConnected, setIsConnected] = useState(true);
    const [isClockRunning, setIsClockRunning] = useState(false);
    const [gamePhase, setGamePhase] = useState('N/A');
    const [roundId, setRoundId] = useState(0);
    const [lastWinnerInfo, setLastWinnerInfo] = useState(null);
    const [winningHistory, setWinningHistory] = useState([]);
    const [appStats, setAppStats] = useState({ totalProfit: 0 });
    const [countdown, setCountdown] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showHistory, setShowHistory] = useState(false);

    const [currentBias, setCurrentBias] = useState(2);
    const [selectedBias, setSelectedBias] = useState(2);
    const [rareLimit, setRareLimit] = useState(30000);
    const [commonLimit, setCommonLimit] = useState(148000);
    const [lowBetProb, setLowBetProb] = useState(70);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [settingsMessage, setSettingsMessage] = useState('');

    const connectSocket = async () => {
        const auth = getAuth();
        if (!auth.currentUser) return;
        try {
            const token = await auth.currentUser.getIdToken(true);
            if (socketRef.current) socketRef.current.disconnect();
            const newSocket = io(SOCKET_URL, { auth: { token }, transports: ['websocket', 'polling'], reconnection: true, reconnectionAttempts: Infinity, reconnectionDelay: 1000 });
            newSocket.on('connect', () => setIsConnected(true));
            newSocket.on('disconnect', () => setIsConnected(false));
            newSocket.on('gameStateUpdate', (state) => {
                setGamePhase(state.gamePhase);
                setRoundId(state.roundId);
                setLastWinnerInfo(state.lastWinnerInfo);
                setWinningHistory(state.winningHistory || []);
                if (state.timerInfo && state.timerInfo.isClockRunning) {
                    const elapsed = (Date.now() - state.timerInfo.phaseStartTime) / 1000;
                    setCountdown(Math.ceil(Math.max(0, state.timerInfo.phaseDuration - elapsed)));
                } else { setCountdown(0); }
            });
            socketRef.current = newSocket;
        } catch (err) { console.error(err); }
    };

    useEffect(() => {
        connectSocket();
        const handleOnline = () => connectSocket();
        const handleOffline = () => setIsConnected(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        const interval = setInterval(() => setCountdown(prev => prev > 0 ? prev - 1 : 0), 1000);
        return () => {
            if (socketRef.current) socketRef.current.disconnect();
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        let alive = true;
        let seeded = false;
        const loadAdminData = async () => {
            try {
                const [data, statsRes] = await Promise.all([
                    apiGet(`/api/games/${GAME_KEY}/settings`),
                    apiGet(`/api/games/${GAME_KEY}/stats`),
                ]);
                if (!alive) return;
                if (data) {
                    setIsClockRunning(data.isClockRunning || false);
                    // Seed editable inputs once so polling doesn't clobber typing.
                    if (!seeded) {
                        setRareLimit(data.rareLimit || 30000);
                        setCommonLimit(data.commonLimit || 148000);
                        setLowBetProb(data.lowBetProbability || 70);
                        if (data.currentRtpLevel) { setCurrentBias(data.currentRtpLevel); setSelectedBias(data.currentRtpLevel); }
                        seeded = true;
                    } else if (data.currentRtpLevel) {
                        setCurrentBias(data.currentRtpLevel);
                    }
                }
                if (statsRes?.stats) setAppStats(statsRes.stats);
            } catch (e) { /* transient — keep last known values */ }
        };
        loadAdminData();
        const poll = setInterval(loadAdminData, 3000);
        return () => { alive = false; clearInterval(poll); };
    }, []);

    const handleToggleClock = async () => {
        setLoading(true); setError('');
        try { await apiPatch(`/api/games/${GAME_KEY}/settings`, { isClockRunning: !isClockRunning }); }
        catch (err) { setError(err.message); }
        setLoading(false);
    };

    const handleResetRoundId = async () => {
        if (!window.confirm('Round ID 0 করতে চান? Round history, winningHistory আর last winner reset হবে। Undo করা যাবে না।')) return;
        try { await apiPost(`/api/games/${GAME_KEY}/reset-round`); alert('Round ID reset triggered.'); }
        catch (err) { alert('Reset failed: ' + err.message); }
    };

    const handleClearHistory = async () => {
        if (!window.confirm('সব পুরনো dailyGameHistory_greedyItems delete হবে। নিশ্চিত?')) return;
        try { await apiPost(`/api/games/${GAME_KEY}/clear-history`); alert('Clear History triggered.'); }
        catch (err) { alert('Clear failed: ' + err.message); }
    };

    const handleSaveSettings = async (e) => {
        e.preventDefault(); setSettingsLoading(true); setSettingsMessage('');
        try {
            const updateData = { rareLimit: Number(rareLimit), commonLimit: Number(commonLimit), lowBetProbability: Number(lowBetProb) };
            if (selectedBias !== currentBias) { updateData.currentRtpLevel = selectedBias; updateData.gameModeBag = []; }
            await apiPatch(`/api/games/${GAME_KEY}/settings`, updateData);
            setSettingsMessage('Settings updated successfully!');
            setTimeout(() => setSettingsMessage(''), 3000);
        } catch (err) { setError(err.message); }
        setSettingsLoading(false);
    };

    const getPhaseColor = (phase) => ({ betting: '#3b82f6', pause: '#6b7280', spinning: '#a78bfa', winning: '#f59e0b', nextRound: '#6366f1' }[phase] || '#ef4444');

    const getSectionUI = (sectionId) => {
        const section = WHEEL_SECTIONS_CONFIG.find(s => s.id === sectionId);
        if (!section) return { name: 'N/A', bg: '#374151' };
        let bg = '#22c55e';
        if (section.payout >= 45) bg = '#ef4444';
        else if (section.payout >= 25) bg = '#f59e0b';
        else if (section.payout >= 15) bg = '#3b82f6';
        else if (section.payout >= 10) bg = '#a78bfa';
        return { name: section.displayText, bg };
    };

    return (
        <div className="dark-page" style={{ position: 'relative' }}>
            {/* Offline Banner */}
            {!isConnected && (
                <div style={{ background: '#dc2626', color: '#fff', textAlign: 'center', padding: '10px', fontWeight: 700, borderRadius: 8, marginBottom: 12 }}>
                    No Internet Connection. Game updates paused. Waiting to reconnect...
                </div>
            )}

            {/* Header */}
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>GreedyItems Game Controller 🎰</h2>
                <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: isConnected ? 'rgba(59,130,246,0.25)' : 'rgba(107,114,128,0.25)', color: isConnected ? '#60a5fa' : '#9ca3af', border: '1px solid rgba(59,130,246,0.4)' }}>
                        {isConnected ? 'ONLINE' : 'OFFLINE'}
                    </span>
                    <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: isClockRunning ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)', color: isClockRunning ? '#4ade80' : '#f87171', border: `1px solid ${isClockRunning ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)'}` }}>
                        {isClockRunning ? 'Game is Running' : 'Game is Stopped'}
                    </span>
                </div>
            </div>

            {/* Stat Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
                {[
                    { label: 'CURRENT PHASE', value: gamePhase.toUpperCase(), color: getPhaseColor(gamePhase) },
                    { label: 'TIME REMAINING', value: `${countdown}s`, color: '#f59e0b' },
                    { label: 'ROUND ID', value: roundId, color: '#a78bfa' },
                    { label: 'APP NET PROFIT', value: formatNumberPrecise(appStats.totalProfit), color: '#4ade80' },
                ].map((s, i) => (
                    <div key={i} className="dark-card" style={{ borderTop: `3px solid ${s.color}`, textAlign: 'center' }}>
                        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 700, marginBottom: 8 }}>{s.label}</p>
                        <p style={{ fontSize: i === 1 ? 28 : 22, fontWeight: 900, color: s.color, margin: 0, fontFamily: 'monospace' }}>{s.value}</p>
                    </div>
                ))}
            </div>

            {/* Advanced Settings */}
            <div className="dark-card" style={{ marginBottom: 16 }}>
                <h3 style={{ textAlign: 'center', marginBottom: 20, fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>Advanced Game Settings</h3>
                <form onSubmit={handleSaveSettings}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 20 }}>
                        {[
                            { label: 'Fish Section Limit', value: rareLimit, setter: setRareLimit },
                            { label: 'Mashala Section Limit', value: commonLimit, setter: setCommonLimit },
                            { label: 'Low Bet Probability %', value: lowBetProb, setter: setLowBetProb },
                        ].map((field, i) => (
                            <div key={i}>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'rgba(255,255,255,0.6)' }}>{field.label}</label>
                                <input type="number" value={field.value} onChange={e => field.setter(e.target.value)} disabled={!isConnected} style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#f1f5f9', fontSize: 14 }} />
                            </div>
                        ))}
                    </div>
                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16, textAlign: 'center' }}>
                        <p style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'rgba(255,255,255,0.7)' }}>Current RTP/Bias: <strong style={{ color: '#ff4d6d' }}>{currentBias}%</strong></p>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 16 }}>
                            {BIAS_OPTIONS.map(p => (
                                <label key={p} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 16px', borderRadius: 8, background: selectedBias === p ? 'rgba(255,77,109,0.2)' : 'rgba(255,255,255,0.06)', border: `1px solid ${selectedBias === p ? 'rgba(255,77,109,0.4)' : 'rgba(255,255,255,0.1)'}`, color: selectedBias === p ? '#ff4d6d' : 'rgba(255,255,255,0.6)', fontWeight: 700 }}>
                                    <input type="radio" name="bias" value={p} checked={selectedBias === p} onChange={e => setSelectedBias(parseInt(e.target.value))} disabled={!isConnected} style={{ accentColor: '#ff4d6d' }} />
                                    {p}%
                                </label>
                            ))}
                        </div>
                        <button type="submit" disabled={settingsLoading || !isConnected} className="btn-upload" style={{ padding: '12px 40px', opacity: (settingsLoading || !isConnected) ? 0.5 : 1 }}>
                            {settingsLoading ? 'Saving...' : 'Save All Settings'}
                        </button>
                        {settingsMessage && <p style={{ color: '#4ade80', marginTop: 10, fontWeight: 700 }}>{settingsMessage}</p>}
                    </div>
                </form>
            </div>

            {/* Winning History */}
            {winningHistory.length > 0 && (
                <div className="dark-card" style={{ marginBottom: 16 }}>
                    <h3 style={{ textAlign: 'center', fontSize: 13, fontWeight: 700, marginBottom: 12, color: 'rgba(255,255,255,0.5)' }}>WINNING HISTORY</h3>
                    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 8 }}>
                        {winningHistory.slice(0, 10).map((entry, index) => {
                            const { name, bg } = getSectionUI(entry.section);
                            return (
                                <div key={`${entry.id}-${index}`} style={{ padding: '6px 12px', borderRadius: 8, background: bg, color: '#fff', fontWeight: 700, fontSize: 12, textAlign: 'center', minWidth: 80 }}>
                                    {name}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Last Winner */}
            {lastWinnerInfo && (
                <div className="dark-card" style={{ marginBottom: 16, textAlign: 'center', borderTop: '3px solid #4ade80' }}>
                    <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 700 }}>LAST ROUND WINNER</p>
                    <p style={{ fontSize: 24, fontWeight: 900, color: '#4ade80', margin: '6px 0 4px' }}>SECTION {lastWinnerInfo.winnerSection?.toUpperCase()}</p>
                    <p style={{ fontSize: 16, color: '#60a5fa', margin: 0, fontWeight: 700 }}>Multiplier: {lastWinnerInfo.payout}x</p>
                </div>
            )}

            {/* Daily History Toggle */}
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
                <button onClick={() => setShowHistory(!showHistory)} style={{ padding: '10px 24px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, fontWeight: 700, color: '#f1f5f9', cursor: 'pointer', fontSize: 14 }}>
                    {showHistory ? 'Hide Daily History' : 'Show Daily History'}
                </button>
            </div>
            {showHistory && <div style={{ marginBottom: 16 }}><DailyHistoryViewer gameType="greedyItems" /></div>}

            {/* Control Buttons */}
            <div style={{ marginBottom: 12 }}>
                <button onClick={handleToggleClock} disabled={loading || !isConnected} style={{
                    width: '100%', padding: '16px', borderRadius: 12, fontWeight: 900, fontSize: 16, color: '#fff', border: 'none', cursor: (loading || !isConnected) ? 'not-allowed' : 'pointer',
                    background: isClockRunning ? 'linear-gradient(135deg,#ef4444,#dc2626)' : 'linear-gradient(135deg,#22c55e,#16a34a)',
                    opacity: (loading || !isConnected) ? 0.5 : 1
                }}>
                    {loading ? 'Processing...' : (isClockRunning ? 'Stop Game' : 'Start Game')}
                </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <button onClick={handleResetRoundId} style={{ padding: '12px', borderRadius: 10, fontWeight: 700, color: '#fff', border: 'none', background: 'linear-gradient(135deg,#f97316,#ea580c)', cursor: 'pointer' }}>
                    RESET ROUND ID
                </button>
                <button onClick={handleClearHistory} style={{ padding: '12px', borderRadius: 10, fontWeight: 700, color: '#fff', border: 'none', background: 'linear-gradient(135deg,#e11d48,#be123c)', cursor: 'pointer' }}>
                    CLEAR ALL HISTORY
                </button>
            </div>
            {error && <div className="status-error" style={{ marginTop: 12 }}>{error}</div>}
        </div>
    );
};

export default Greedy;
